/**
 * Automatically closes ArcGIS Identity Manager authentication dialogs
 *
 * ArcGIS IdentityManager creates calcite-dialog elements for authentication.
 * These dialogs have a focus-trap that blocks all clicks on the page.
 * This service automatically closes these dialogs to prevent UI blocking.
 *
 * IMPORTANT: This is a workaround. You should fix the root cause by:
 * 1. Adding an ArcGIS API key: esriConfig.apiKey = 'YOUR_KEY'
 * 2. Removing protected resources that require authentication
 * 3. Providing credentials via IdentityManager.registerToken()
 */

import { Injectable, OnDestroy } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

@Injectable({
  providedIn: 'root'
})
export class DialogAutoCloseService implements OnDestroy {
  private observer: MutationObserver | null = null;
  private hasWarned = false;

  constructor(private readonly logger: NGXLogger) {}

  /**
   * Start monitoring for ArcGIS authentication dialogs
   */
  initialize(): void {
    this.logger.info('[DialogAutoCloseService] Initializing auto-close for ArcGIS auth dialogs');

    this.observer = new MutationObserver((mutations) => {
      this.checkAndCloseAuthDialogs();
    });

    // Watch for new elements being added to the DOM
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open']
    });

    // Also check immediately in case dialog is already present
    setTimeout(() => this.checkAndCloseAuthDialogs(), 0);
  }

  /**
   * Check for ArcGIS authentication dialogs and close them
   */
  private checkAndCloseAuthDialogs(): void {
    const dialogs = document.querySelectorAll('calcite-dialog[open]');

    dialogs.forEach((dialog) => {
      const textContent = dialog.textContent || '';

      // Check if this is an ArcGIS authentication dialog
      if (
        textContent.includes('ArcGIS Online') ||
        textContent.includes('sign in to access') ||
        textContent.includes('Please sign in')
      ) {
        this.logger.warn(
          '[DialogAutoCloseService] Detected ArcGIS auth dialog - closing automatically',
          {
            dialogText: textContent.substring(0, 100)
          }
        );

        // Close the dialog
        dialog.removeAttribute('open');

        // Warn once about the underlying issue
        if (!this.hasWarned) {
          this.hasWarned = true;
          this.logger.error(
            '⚠️ CONFIGURATION ISSUE: ArcGIS authentication dialog detected!\n' +
            'Your application is trying to access a protected ArcGIS Online resource.\n' +
            'This dialog has been auto-closed to prevent UI blocking.\n\n' +
            'To fix this permanently, choose ONE of these options:\n' +
            '1. Add an ArcGIS API key: esriConfig.apiKey = "YOUR_KEY"\n' +
            '2. Remove the protected resource from your configuration\n' +
            '3. Provide credentials via IdentityManager.registerToken()\n' +
            '4. Disable identity prompts: esriConfig.portalUrl = null\n\n' +
            'See BUTTON_CLICKABILITY_FINAL_SOLUTION.md for details.'
          );
        }
      }
    });
  }

  /**
   * Stop monitoring and cleanup
   */
  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
      this.logger.debug('[DialogAutoCloseService] Stopped monitoring');
    }
  }
}
